import { CreditCardOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Grid, Modal, Spin } from "antd";
import { AxiosError } from "axios";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { notification } from "shared/lib/antd-global";
import { attachCard, syncCard } from "../api";
import styles from "./index.module.scss";

type CardAttachButtonProps = {
  block?: boolean;
};

export const CardAttachButton = ({ block }: CardAttachButtonProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [frameUrl, setFrameUrl] = useState<string | null>(null);
  const [isAttaching, setIsAttaching] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [isLoadingFrame, setIsLoadingFrame] = useState(true);

  const screens = Grid.useBreakpoint();
  const isMobile = screens.xs === true;

  const { mutate: attach, isPending: isAttachPending } = useMutation({
    mutationFn: () => attachCard(),
    onSuccess(response) {
      if (response.success) {
        setFrameUrl(response.frame_url);
        setIsModalVisible(true);
        setIsAttaching(false);
      } else {
        notification.error({ title: t("cards.attach_error") });
        setIsAttaching(false);
      }
    },
    onError(error: AxiosError<{ message: string }>) {
      notification.error({
        title: error?.response?.data?.message || t("cards.attach_error"),
        duration: 8,
      });
      setIsAttaching(false);
    },
  });

  const { mutate: sync } = useMutation({
    mutationFn: () => syncCard(),
    onSuccess(response) {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ["cards"] });
        notification.success({ title: t("cards.cards_synced") });
      } else {
        notification.error({ title: t("cards.sync_error") });
      }
    },
    onError(error: AxiosError<{ message: string }>) {
      notification.error({
        title: error?.response?.data?.message || t("cards.sync_error"),
        duration: 12,
      });
    },
  });

  const handleAttachCard = () => {
    setIsAttaching(true);
    attach();
  };

  useEffect(() => {
    const FRAME_TYPE = "linking";
    const FRAME_RESULT_SUCCESS = 1;
    const FRAME_RESULT_ERROR = 3;
    const FRAME_RESULT_AUTHORISATION_ERROR = 2;
    const FRAME_RESULT_LOAD = 4;

    const handleMessage = (event: MessageEvent) => {
      try {
        const message =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;

        if (message && message.source === FRAME_TYPE) {
          switch (message.status) {
            case FRAME_RESULT_LOAD:
              setIsLoadingFrame(false);
              break;

            case FRAME_RESULT_SUCCESS:
              setIsModalVisible(false);
              notification.success({ title: t("cards.card_attached_success") });
              sync();
              break;

            case FRAME_RESULT_ERROR:
              setIsModalVisible(false);
              notification.error({ title: t("cards.attach_error") });
              break;

            case FRAME_RESULT_AUTHORISATION_ERROR:
              setIsModalVisible(false);
              notification.error({ title: t("cards.attach_error") });
              break;

            default:
              console.log("Unknown status:", message.status);
          }
        }
      } catch {}
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [sync, t]);

  useEffect(() => {
    if (!isModalVisible) {
      setIsLoadingFrame(true);
    }
  }, [isModalVisible]);

  return (
    <>
      <Button
        type="primary"
        icon={<CreditCardOutlined />}
        onClick={handleAttachCard}
        loading={isAttachPending || isAttaching}
        disabled={isAttachPending || isAttaching}
        block={block}
      >
        {t("cards.add_card_button")}
      </Button>

      <Modal
        title={t("cards.add_card_modal_title")}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setIsAttaching(false);
        }}
        style={
          isMobile
            ? {
                top: "calc(28px + var(--safe-area-inset-top, env(safe-area-inset-top, 0px)))",
              }
            : {}
        }
        footer={null}
        width={600}
        destroyOnHidden
      >
        {frameUrl && (
          <div
            style={{
              height: isMobile
                ? "calc(100vh - 128px - 28px - var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) - var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))"
                : "600px",
              position: "relative",
            }}
          >
            {isLoadingFrame && (
              <div className={styles.modalWrapperLoader}>
                <Spin />
              </div>
            )}

            <iframe
              ref={iframeRef}
              src={frameUrl}
              width="100%"
              height="100%"
              style={{ border: "none" }}
              title="Card Attach"
              sandbox="allow-scripts allow-modals allow-same-origin allow-forms allow-popups"
            />
          </div>
        )}
      </Modal>
    </>
  );
};
